import { useCallback } from "react";
import { EmailRecipient } from "../ClientEmailSelector";
import { Client } from "@/lib/types/database";
import { User } from "@/lib/types/user";
import { addRecent } from "@/components/ui/responsive-select/recents";
import { isValidEmail } from "../utils/email-validation";

export const useRecipientActions = (
  selectedRecipients: EmailRecipient[],
  onRecipientsChange: (recipients: EmailRecipient[]) => void,
  setInputValue: (value: string) => void,
  setIsDropdownOpen: (open: boolean) => void
) => {
  const addClientRecipient = useCallback(
    (client: Client) => {
      if (!client.email) return;
      const exists = selectedRecipients.find((r) => r.email === client.email);
      if (exists) return;
      const newRecipient: EmailRecipient = {
        id: client.id,
        type: "client",
        email: client.email,
        name: client.name || client.company_name || undefined,
        client,
      };
      onRecipientsChange([...selectedRecipients, newRecipient]);
      addRecent("contacts", { id: client.id, label: client.name || client.company_name || client.email }, 8);
      setInputValue("");
      setIsDropdownOpen(false);
    },
    [selectedRecipients, onRecipientsChange, setInputValue, setIsDropdownOpen]
  );

  const addHubUserRecipient = useCallback(
    (user: User) => {
      if (!user.email) return;
      const exists = selectedRecipients.find((r) => r.email === user.email);
      if (exists) return;
      const displayName = [user.first_name, user.last_name].filter(Boolean).join(" ") || user.email;
      const newRecipient: EmailRecipient = {
        id: user.id,
        type: "hub_user",
        email: user.email,
        name: displayName,
        hubUser: user,
      };
      onRecipientsChange([...selectedRecipients, newRecipient]);
      setInputValue("");
      setIsDropdownOpen(false);
    },
    [selectedRecipients, onRecipientsChange, setInputValue, setIsDropdownOpen]
  );

  const addEmailRecipient = useCallback(
    (email: string) => {
      if (!isValidEmail(email)) return;
      const exists = selectedRecipients.find((r) => r.email === email);
      if (exists) return;
      const newRecipient: EmailRecipient = {
        id: `email-${Date.now()}-${Math.random()}`,
        type: "email",
        email,
      };
      onRecipientsChange([...selectedRecipients, newRecipient]);
      setInputValue("");
    },
    [selectedRecipients, onRecipientsChange, setInputValue]
  );

  const removeRecipient = useCallback(
    (recipientId: string) => {
      onRecipientsChange(selectedRecipients.filter((r) => r.id !== recipientId));
    },
    [selectedRecipients, onRecipientsChange]
  );

  return { addClientRecipient, addHubUserRecipient, addEmailRecipient, removeRecipient };
};
